import { CssPixel, SymbolType } from "@spawn/common";
import { GameParams, service } from "@spawn/core";
import {
	GoldenBonusOverlayView,
	GoldenBonusReelView,
	GoldenBonusService,
	GoldenBonusState,
	GoldenBonusSymbolTypes,
} from "@spawn/golden-bonus";
import { ModernUiParams, PageService, Presets } from "@spawn/modern-ui";
import { NudgingOverlayView, NudgingService } from "@spawn/nudge";
import { RevealService } from "@spawn/reveal";
import {
	CasinoOptionsService,
	GameConfigService,
	LaglessGenerator,
	LineMarkerView,
	LinePingerView,
	LineStepperView,
	NetworkConfigProvider,
	ParamPresets,
	PaytableService,
	ScatterModule,
	SymbolMetaDataKeys,
	TeaserBellModule,
	WinPartyState,
} from "@spawn/slot-machine";
import Victor from "victor";
import { AlternatingLineStepperModule, NrgLineStepperModule, fgLaglessSpin, goldenBonusLaglessSpinConfig, laglessSpin, LineWinView, ReelSetName } from "@spawn/nrg-games-core";
import { HelpPage, PaytablePage } from "./pages/pages";
import { BaseSymbolTypes, convertBraggIdToSymbolTypeId, convertCubeIdToSymbolType } from "./services/symbolService";


@service
export class Params extends GameParams {
	override presets = [ParamPresets.fullHd(), ParamPresets.commonValues(), Presets.fullHd()];

	constructor() {
		super();

		this.bindDescriptors(ModernUiParams, ({ create }) => {
			create("menuDesktopPositionTop", new CssPixel(60));
			create("pageSeparatorHeightMobile", new CssPixel(4));
			create("pageSymbolMaxWidthMobile", new CssPixel(200));
			create("pageSymbolMaxWidthDesktop", new CssPixel(150));
			create("pageTextImageSizeDesktop", new CssPixel(50));
			create("pageTextImageSizeMobile", new CssPixel(90));
		});

		this.bindDescriptors(PageService, ({ create }) => {
			create("paytablePage", PaytablePage);
			create("helpPage", HelpPage);
		});

		this.bindDescriptors(NetworkConfigProvider, ({ create }) => {
			create("cubeBackendConfig", {
				visibleSymbolCount: 4,
				convertToSymbolType: (cubeId: string): SymbolType => convertCubeIdToSymbolType(cubeId),
				gameId: "flordemuerto",
			});

			create("braggBackendConfig", {
				visibleSymbolCount: 4,
				convertToSymbolType: (braggId: number): SymbolType => convertBraggIdToSymbolTypeId(braggId),
				gameId: "GAM_FDM",
			});
		});

		this.bindDescriptors(GameConfigService, ({ create }) => {
			create("defaultConfig", {
				reelCount: 5,
				spaceBetweenReels: 5,
				swingOffSymbols: 1,
				swingOnSymbols: 1,
				symbolSize: new Victor(264, 183),
				visibleSymbols: 4,
			});
		});

		this.bindDescriptors(CasinoOptionsService, ({ create }) => {
			create("winChancesByRtpAndLines", [
				{ rtp: "88", lines: 20, winChance: "1:52697655061", winFactor: 258.5, displayRtp: "88.21" },
				{ rtp: "90", lines: 20, winChance: "1:52770355892", winFactor: 258.5, displayRtp: "90.21" },
				{ rtp: "92", lines: 20, winChance: "1:52842387230", winFactor: 258.5, displayRtp: "92.11" },
				{ rtp: "94", lines: 20, winChance: "1:52910856713", winFactor: 258.5, displayRtp: "94.11" },
				{ rtp: "96", lines: 20, winChance: "1:52976663511", winFactor: 258.5, displayRtp: "96.12" },
			]);
		});

		this.bindDescriptors(LaglessGenerator, ({ create }) => {
			create("laglessSpinConfigs", {
				[ReelSetName.BaseGame]: { symbols: laglessSpin.symbols, criticalSymbols: laglessSpin.criticalSymbols },
				[ReelSetName.FreeGame]: { symbols: fgLaglessSpin.symbols, criticalSymbols: fgLaglessSpin.criticalSymbols },
			});
		});

		this.bindDescriptors(NrgLineStepperModule, ({ create }) => {
			create("visibleMetaDataKeys", [SymbolMetaDataKeys.WinAnimation]);
		});
		this.bindDescriptors(AlternatingLineStepperModule, ({ create }) => {
			create("visibleMetaDataKeysBySymbol", { [BaseSymbolTypes.ScatterFG]: ["BonusAnimationExtra"] });
			create("visibleMetaDataKeys", [SymbolMetaDataKeys.WinAnimation]);
			create("lineStepDuration", 2.75);
		});
		this.bindDescriptors(TeaserBellModule, ({ create }) => {
			create("visibleMetaDataKeys", [SymbolMetaDataKeys.WinAnimation]);
		});

		this.bindDescriptors(LineMarkerView, ({ create }) => {
			create("lineMarkerOffset", new Victor(100, 40));
		});

		this.bindDescriptors(LineWinView, ({ create }) => {
			create("winTextShadowOpacity", 0.8);
		});

		this.bindDescriptors(LineStepperView, ({ create }) => {
			create("lineStepperOffset", new Victor(-80, 0));
		});

		this.bindDescriptors(PaytableService, ({ create }) => {
			create("paytableData", {
				20: {
					[BaseSymbolTypes.LV4]: [
						{ label: "5", multiplier: 50 },
						{ label: "4", multiplier: 10 },
						{ label: "3", multiplier: 2 },
					],
					[BaseSymbolTypes.LV3]: [
						{ label: "5", multiplier: 50 },
						{ label: "4", multiplier: 10 },
						{ label: "3", multiplier: 2 },
					],
					[BaseSymbolTypes.LV2]: [
						{ label: "5", multiplier: 50 },
						{ label: "4", multiplier: 10 },
						{ label: "3", multiplier: 2 },
					],
					[BaseSymbolTypes.LV1]: [
						{ label: "5", multiplier: 50 },
						{ label: "4", multiplier: 10 },
						{ label: "3", multiplier: 2 },
					],
					[BaseSymbolTypes.HV4]: [
						{ label: "5", multiplier: 150 },
						{ label: "4", multiplier: 30 },
						{ label: "3", multiplier: 10 },
					],
					[BaseSymbolTypes.HV3]: [
						{ label: "5", multiplier: 200 },
						{ label: "4", multiplier: 40 },
						{ label: "3", multiplier: 10 },
					],
					[BaseSymbolTypes.HV2]: [
						{ label: "5", multiplier: 300 },
						{ label: "4", multiplier: 60 },
						{ label: "3", multiplier: 15 },
					],
					[BaseSymbolTypes.HV1]: [
						{ label: "5", multiplier: 500 },
						{ label: "4", multiplier: 100 },
						{ label: "3", multiplier: 20 },
					],
					[BaseSymbolTypes.Top]: [
						{ label: "5", multiplier: 1000 },
						{ label: "4", multiplier: 200 },
						{ label: "3", multiplier: 40 },
					],
					[BaseSymbolTypes.Scatter]: [
						{ label: "6", multiplier: 30 },
						{ label: "5", multiplier: 30 },
						{ label: "4", multiplier: 30 },
						{ label: "3", multiplier: 30 },
					],
				},
			});
		});

		this.bindDescriptors(LinePingerView, ({ create }) => {
			create("stripeOffset", new Victor(-60, 70));
			create("stripeDownOffset", new Victor(122, 55));
		});

		// REVEAL
		this.bindDescriptors(RevealService, ({ create }) => {
			create("revealSoundDelay", 0);
			create("interRevealDelay", 0);
			create("uncoverAnimationDurationNormal", 0.5);
			create("coverAnimationDuration", 0.5);
			create("revealAnimationDuration", 1);
			create("mysterySymbol", BaseSymbolTypes.Mystery);
			create("mysteryBlockSize", 4);
		});

		// GOLDEN BONUS
		this.bindDescriptors(GoldenBonusReelView, ({ create }) => {
			create("laglessSpinConfig", goldenBonusLaglessSpinConfig);
			create("triggerAnimationSequence", [{ animationName: "golden-bonus_reveal", skinName: "golden-bonus_reveal" }]);
			create("symbolsPerSecond", 7.5);
			create("reelSpinDurationInSeconds", 1.9);
			create("reelSpinStopDelay", 1.2);
		});
		this.bindDescriptors(GoldenBonusOverlayView, ({ create }) => {
			create("delayBeforeSpin", 1.2);
		});
		this.bindDescriptors(GoldenBonusService, ({ create }) => {
			create("bonusSymbolTypes", [...Object.values(GoldenBonusSymbolTypes), BaseSymbolTypes.Scatter, BaseSymbolTypes.ScatterFG]);
		});
		this.bindDescriptors(GoldenBonusState, ({ create }) => {
			create("fadeAmbientSound", false);
		});

		// NUDGING
		this.bindDescriptors(NudgingOverlayView, ({ create }) => {
			create("easingFn", "easeInQuadBounce");
		});
		this.bindDescriptors(NudgingService, ({ create }) => {
			create("nudgingDurationMs", 300);
			create("nudgingSpinDelayMs", 0)
		});

		// Freegames
		this.bindDescriptors(ScatterModule, ({ create }) => {
			create("animationDuration", 5);
		});
		this.bindDescriptors(WinPartyState, ({ create }) => {
			create("showScatterAnimation", true);
		});
	}
}
